import {
  Table,
  Badge,
  Select,
  Anchor,
  Group,
  Text,
  TextInput,
  Tooltip,
  ActionIcon,
  Stack,
} from "@mantine/core";
import { IconExternalLink, IconPlus, IconTrash } from "@tabler/icons-react";
import { memo, useState, useCallback, useRef } from "react";

const STATUS_OPTIONS = [
  { value: "NotStarted", label: "Not Started" },
  { value: "Submitted", label: "Submitted" },
  { value: "PendingVerification", label: "Pending Verification" },
  { value: "Verified", label: "Verified" },
  { value: "Live", label: "Live" },
  { value: "Rejected", label: "Rejected" },
];

const STATUS_COLORS = {
  NotStarted: "gray",
  Submitted: "blue",
  PendingVerification: "yellow",
  Verified: "teal",
  Live: "green",
  Rejected: "red",
};

const DirectoryRow = memo(function DirectoryRow({ dir, index, onUpdate, onRemove }) {
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <Text size="sm" fw={500}>
            {dir.directoryName}
          </Text>
          {dir.directoryUrl && (
            <Anchor href={dir.directoryUrl} target="_blank" size="xs">
              <IconExternalLink size={14} />
            </Anchor>
          )}
        </Group>
        {dir.listingUrl && (
          <Anchor href={dir.listingUrl} target="_blank" size="xs">
            View listing
          </Anchor>
        )}
      </Table.Td>
      <Table.Td>
        {dir.domainRating && (
          <Badge variant="light" size="sm">
            {dir.domainRating}
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {dir.cost || "Free"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Select
          size="xs"
          data={STATUS_OPTIONS}
          value={dir.status}
          onChange={(val) => onUpdate(index, "status", val)}
          styles={{
            input: {
              color: `var(--mantine-color-${STATUS_COLORS[dir.status]}-6)`,
              fontWeight: 500,
            },
          }}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          placeholder="Notes..."
          value={dir.notes || ""}
          onChange={(e) => onUpdate(index, "notes", e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <Tooltip label="Remove">
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={() => onRemove(index)}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
});

export default memo(function DirectoryChecklist({ directories, onChange }) {
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");

  // Stable refs so callbacks don't recreate on every render
  const directoriesRef = useRef(directories);
  directoriesRef.current = directories;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const updateDirectory = useCallback((index, field, value) => {
    const updated = directoriesRef.current.map((dir, i) => {
      if (i !== index) return dir;
      const updatedDir = { ...dir, [field]: value };
      if (field === "status" && value !== "NotStarted" && !dir.submittedAt) {
        updatedDir.submittedAt = new Date().toISOString();
      }
      return updatedDir;
    });
    onChangeRef.current(updated);
  }, []);

  const removeDirectory = useCallback((index) => {
    onChangeRef.current(directoriesRef.current.filter((_, i) => i !== index));
  }, []);

  const addDirectory = () => {
    if (!newName.trim()) return;
    onChangeRef.current([
      ...directoriesRef.current,
      {
        directoryName: newName.trim(),
        directoryUrl: newUrl.trim(),
        status: "NotStarted",
        submittedAt: null,
        listingUrl: null,
        notes: null,
        domainRating: null,
        cost: null,
      },
    ]);
    setNewName("");
    setNewUrl("");
  };

  const completedCount = directories.filter(
    (d) => d.status === "Live" || d.status === "Verified"
  ).length;

  const submittedCount = directories.filter(
    (d) => d.status !== "NotStarted"
  ).length;

  return (
    <Stack gap="sm">
      <Group gap="lg">
        <Badge color="blue" variant="light" size="lg">
          {submittedCount} / {directories.length} submitted
        </Badge>
        <Badge color="green" variant="light" size="lg">
          {completedCount} / {directories.length} live
        </Badge>
      </Group>

      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Directory</Table.Th>
            <Table.Th>DR</Table.Th>
            <Table.Th>Cost</Table.Th>
            <Table.Th w={180}>Status</Table.Th>
            <Table.Th>Notes</Table.Th>
            <Table.Th w={40}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {directories.map((dir, index) => (
            <DirectoryRow
              key={index}
              dir={dir}
              index={index}
              onUpdate={updateDirectory}
              onRemove={removeDirectory}
            />
          ))}
          {/* Add new row */}
          <Table.Tr>
            <Table.Td>
              <TextInput
                size="xs"
                placeholder="Directory name"
                value={newName}
                onChange={(e) => setNewName(e.currentTarget.value)}
                onKeyDown={(e) => e.key === "Enter" && addDirectory()}
              />
            </Table.Td>
            <Table.Td></Table.Td>
            <Table.Td></Table.Td>
            <Table.Td>
              <TextInput
                size="xs"
                placeholder="https://..."
                value={newUrl}
                onChange={(e) => setNewUrl(e.currentTarget.value)}
                onKeyDown={(e) => e.key === "Enter" && addDirectory()}
              />
            </Table.Td>
            <Table.Td colSpan={2}>
              <Tooltip label="Add directory">
                <ActionIcon
                  variant="light"
                  onClick={addDirectory}
                  disabled={!newName.trim()}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Tooltip>
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </Stack>
  );
});
